import React, { FC, Fragment, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { PillTabs } from 'components/PillTabs'
import { Tab } from 'components/PillTabs/Tab'
import { TabLabel } from 'components/PillTabs/TabLabel'
import { useTranslation } from 'react-i18next'
import { useRequestPendingItems, useRequestPendingRiskItems } from 'hooks'
import { isNilOrEmpty } from 'packages/core'
import { Pagination } from 'packages/eid-ui'
import { Search } from 'components'
import { PendingItems } from './PendingItems'
import { Skeleton } from '@mui/material'
import { mobileScreenWidth } from 'utils'
import { RisksWithViolationsByFunctions } from 'components/V2/RiskViolationsByFunctions'

const useStyles = makeStyles(
    createStyles({
        root: {
            marginTop: '1px',
        },
        tabsContainer: {
            display: 'flex',
            [`@media (max-width:${mobileScreenWidth}) `]: {
                display: 'block',
            },
        },
        tabsSearchContainer: {
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: '#FAFAFC',
            borderBottom: 'solid 1px #d8d8dd',
            alignItems: 'flex-end',
            paddingBottom: '8px',
            '& > *': {
                marginRight: '26px',
                width: '309px',
                display: 'flex',
                alignItems: 'center',
                [`@media (max-width:${mobileScreenWidth}) `]: {
                    width: '100%',
                    marginLeft: '26px',
                    marginTop: '8px',
                },
            },
        },
    }),
)

const pendingItemsTake = 10

export interface RequestToDoTabsProps {
    data: any
}

export type TabType =
    | undefined
    | 'pendingRiskItems'
    | 'pendingItems'
    | 'ByRisksFunctions'

const RequestToDoTabs: FC<RequestToDoTabsProps> = (props) => {
    const { t } = useTranslation()

    const classes = useStyles()

    const [activeTab, setActiveTab] = useState<TabType>(undefined)

    const [pendingItemsPage, setPendingItemsPage] = useState(1)
    const [pendingItemsSearchKey, setPendingItemsSearchKey] = useState('')

    const [perPage, setPerPage] = useState(pendingItemsTake)

    const handlePageChange = (value: any) => {
        setPendingItemsPage(value)
    }

    const handlePageSelection = (value: any) => {
        setPendingItemsPage(value)
    }

    const handleItemsPerPageChange = (value: any) => {
        setPendingItemsPage(1)
        setPerPage(value)
    }

    const { data: pendingItemsLatestData, isFetching: isLoadingPendingItems } =
        useRequestPendingItems(
            props.data.id,
            (pendingItemsPage - 1) * perPage,
            perPage,
            pendingItemsSearchKey,
        )

    const pendingItemsTotalCount = pendingItemsLatestData
        ? pendingItemsLatestData.totalCount
        : 0

    const { data: pendingRiskItems, totalCount: pendingRiskItemsTotalCount } =
        useRequestPendingRiskItems(props.data.id)

    useEffect(() => {
        if (
            pendingItemsTotalCount === undefined ||
            pendingRiskItemsTotalCount === undefined
        )
            return

        if (activeTab === undefined) {
            if (pendingRiskItemsTotalCount > 0) {
                setActiveTab('ByRisksFunctions')
            } else {
                setActiveTab('pendingItems')
            }
        }
    }, [activeTab, pendingItemsTotalCount, pendingRiskItemsTotalCount])

    const pendingItems = pendingItemsLatestData?.data

    const pendingItemsNoOfPages = pendingItemsLatestData
        ? Math.ceil(pendingItemsLatestData.totalCount / perPage)
        : 0

    const showPendingItems =
        !pendingItemsLatestData ||
        pendingItemsLatestData.totalCount > 0 ||
        !isNilOrEmpty(pendingItemsSearchKey)

    const showPendingRiskItems =
        !pendingRiskItems || (pendingRiskItemsTotalCount as number) > 0

    if (activeTab === undefined) {
        return (
            <Box marginX={4} marginY={4}>
                <Skeleton animation="wave" variant="rectangular" height={24} />
                <Box marginY={1} />
                <Skeleton animation="wave" variant="rectangular" height={24} />
            </Box>
        )
    }

    if (!showPendingItems && !showPendingRiskItems) {
        return <Fragment />
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.tabsContainer}>
                <PillTabs
                    value={activeTab}
                    onChange={(_, newValue: string) =>
                        setActiveTab(newValue as TabType)
                    }
                >
                    {showPendingRiskItems && (
                        <Tab
                            label={
                                <TabLabel
                                    label={t('RiskViolationsToDoList')}
                                    count={pendingRiskItemsTotalCount}
                                />
                            }
                            value={'ByRisksFunctions'}
                        />
                    )}

                    {showPendingItems && (
                        <Tab
                            label={
                                <TabLabel
                                    label={t('ToDo')}
                                    count={pendingItemsLatestData?.totalCount}
                                />
                            }
                            value={'pendingItems'}
                        />
                    )}
                </PillTabs>
                <Box className={classes.tabsSearchContainer}>
                    {activeTab === 'pendingItems' && showPendingItems && (
                        <Search
                            outlined
                            placeholder={t('Search')}
                            value={pendingItemsSearchKey}
                            handleClear={() => setPendingItemsSearchKey('')}
                            handleSearch={(value: any) => {
                                setPendingItemsPage(1)
                                setPendingItemsSearchKey(value)
                            }}
                        />
                    )}
                </Box>
            </Box>
            {activeTab === 'ByRisksFunctions' && showPendingRiskItems && (
                <Box marginY={'16px'}>
                    <RisksWithViolationsByFunctions
                        requestType="businessRequests"
                        pendingRiskItems={pendingRiskItems}
                        page={'Requests'}
                        showDecisions={true}
                        requestId={props.data.id}
                    />
                </Box>
            )}

            {activeTab === 'pendingItems' && showPendingItems && (
                <Fragment>
                    {!isLoadingPendingItems ? (
                        <>
                            <PendingItems
                                businessRequest={props.data}
                                data={pendingItems}
                                isLoading={!Boolean(pendingItemsLatestData)}
                                id={props.data.id}
                                totalCount={
                                    pendingItemsLatestData &&
                                    pendingItemsLatestData.totalCount
                                }
                                callApiOnEveryChange={
                                    props.data.status === 'Open_PendingApproval'
                                }
                                onSuccess={() => setPendingItemsPage(1)}
                            />

                            <Box display="flex" justifyContent="center" my={2}>
                                <Pagination
                                    count={pendingItemsNoOfPages}
                                    size="large"
                                    page={pendingItemsPage}
                                    onChange={(_: any, newValue: any) =>
                                        handlePageChange(newValue)
                                    }
                                    showPageSelection={true}
                                    onPageSelection={handlePageSelection}
                                    showItemsPerPageSelection={true}
                                    onItemsPerPageChange={
                                        handleItemsPerPageChange
                                    }
                                    itemsPerPage={perPage}
                                    showCountStat={true}
                                    totalCount={
                                        pendingItemsLatestData?.totalCount
                                    }
                                    parentIndex={10}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box marginX={4} marginY={4}>
                            <Skeleton
                                animation="wave"
                                variant="rectangular"
                                height={24}
                            />
                            <Box marginY={1} />
                            <Skeleton
                                animation="wave"
                                variant="rectangular"
                                height={24}
                            />
                        </Box>
                    )}
                </Fragment>
            )}
        </Box>
    )
}

export default RequestToDoTabs
